import { Planner, TodoWithDetails } from '@/common/types/databaseTypes';
import { formatDate } from '@tickitacka/shared';
import { findContiguousRanges } from '@/common/utils/timeBlockUtils';
import {
    formatTime,
    getDurationText,
} from '@/common/utils/timeBlockFormatters';
import { Button, Group, Stack, Tooltip, Text, ActionIcon } from '@mantine/core';
import { IconCalendarEvent, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useCheckSameWorkspaceMembership } from '@/features/workspace/services/useWorkspaceQueries';
import { useAuthStore } from '@/stores/authStore';

interface TodoPlannerListProps {
    todo: TodoWithDetails;
    singlePlanner?: Partial<Planner> | null; // 특정 플래너만 표시할 경우 사용
    isNewTodo?: boolean;
    onPlannerSelect: (planner: Partial<Planner>) => void;
    onDeletePlannerRange: (plannerId: string, rangeToDelete: number[]) => void;
}

export default function TodoPlannerList({
    todo,
    singlePlanner,
    isNewTodo = false,
    onPlannerSelect,
    onDeletePlannerRange,
}: TodoPlannerListProps) {
    const { t } = useTranslation('todo');
    const { user } = useAuthStore();

    const workspaceMembership = useCheckSameWorkspaceMembership(
        todo.user_id,
        user.id
    );

    // 새로운 Todo이거나 플래너가 없는 경우 null 반환
    if (isNewTodo || !todo?.planners?.length) return null;

    todo?.planners.sort(
        (a, b) =>
            new Date(a.planner_date).getTime() -
            new Date(b.planner_date).getTime()
    );

    // singlePlanner가 제공되면 그것만 사용, 아니면 todo.planners 전체 사용
    const plannersToRender: Partial<Planner>[] = singlePlanner
        ? [singlePlanner]
        : todo?.planners || [];

    return (
        <Stack gap="xs" w="100%">
            {plannersToRender.map(planner => {
                const timeRanges = findContiguousRanges(planner.blocks);
                const isEditable = todo.user_id === user?.id || workspaceMembership.data?.isSameWorkspace;

                return (
                    <Group key={planner.id} justify="space-between">
                        {isEditable ? (
                            <Tooltip
                                label={t(
                                    'component.todoDrawer.planner_list.set_time'
                                )}
                                withArrow
                            >
                                <Button
                                    variant="subtle"
                                    color="gray"
                                    size="xs"
                                    onClick={() => onPlannerSelect(planner)}
                                >
                                    <Group gap={'4px'}>
                                        <IconCalendarEvent size="14px" />
                                        <Text size="xs" c="gray.9">
                                            {formatDate(
                                                new Date(planner.planner_date),
                                                {
                                                    showYear: false,
                                                    showWeekday: false,
                                                }
                                            )}
                                        </Text>
                                    </Group>
                                </Button>
                            </Tooltip>
                        ) : (
                            <Group gap={'4px'} pl="xs">
                                <IconCalendarEvent size="14px" />
                                <Text size="xs" c="gray.9">
                                    {formatDate(
                                        new Date(planner.planner_date),
                                        {
                                            showYear: false,
                                            showWeekday: false,
                                        }
                                    )}
                                </Text>
                            </Group>
                        )}

                        <Stack gap={2} align="end">
                            {timeRanges.map(timeRange => {
                                const startTime = Math.min(...timeRange);
                                const endTime = Math.max(...timeRange);
                                const timeString = `${formatTime(startTime)} ~ ${formatTime(endTime + 1)} (${getDurationText(timeRange)})`;

                                return (
                                    <Group key={timeRange[0]} gap={4}>
                                        <Text size="xs" c="gray.8" ta="right">
                                            {timeString}
                                        </Text>
                                        {isEditable && (
                                            <Tooltip
                                                label={t(
                                                    'component.todoDrawer.planner_list.delete_time'
                                                )}
                                                withArrow
                                            >
                                                <ActionIcon
                                                    onClick={() => {
                                                        onDeletePlannerRange(
                                                            planner.id,
                                                            timeRange
                                                        );
                                                    }}
                                                    size="xs"
                                                    variant="subtle"
                                                    color="gray"
                                                >
                                                    <IconX />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </Group>
                                );
                            })}
                        </Stack>
                    </Group>
                );
            })}
        </Stack>
    );
}
